import React, { useState, useEffect } from 'react'
import { Card, Table, Select, DatePicker, Button, Tag, Statistic, Row, Col, Modal, message } from 'antd'
import { DownloadOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { VITE_API_BASE as API_BASE } from '../config/apiConfig'

const { RangePicker } = DatePicker
const { Option } = Select

const OperationLogs = () => {
  const [logs, setLogs] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 })
  const [filters, setFilters] = useState({ action: '', startDate: '', endDate: '' })

  useEffect(() => {
    loadLogs()
    loadStats()
  }, [])

  const loadLogs = async (page = 1) => {
    setLoading(true)
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      })
      if (filters.action) params.append('action', filters.action)
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate) params.append('endDate', filters.endDate)

      const res = await fetch(`${API_BASE}/admin/logs?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      const data = await res.json()
      
      if (data.success) {
        setLogs(data.data)
        setPagination(prev => ({ ...prev, current: page, total: data.total }))
      }
    } catch (err) {
      message.error('加载失败')
    }
    setLoading(false)
  }

  const loadStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/logs/stats`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      const data = await res.json()
      if (data.success) setStats(data.data)
    } catch (err) { console.error(err) }
  }

  const handleSearch = () => {
    loadLogs(1)
  }

  const handleExport = async () => {
    try {
      const params = new URLSearchParams()
      if (filters.action) params.append('action', filters.action)
      if (filters.startDate) params.append('startDate', filters.startDate)
      if (filters.endDate) params.append('endDate', filters.endDate)

      const res = await fetch(`${API_BASE}/admin/logs/export?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      })
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `操作日志_${new Date().toISOString().substring(0,10)}.csv`
      link.click()
      message.success('导出成功')
    } catch (err) {
      message.error('导出失败')
    }
  }

  const handleCleanup = () => {
    Modal.confirm({
      title: '确认清理',
      content: '确定要清理90天前的日志吗？此操作不可恢复。',
      onOk: async () => {
        try {
          const res = await fetch(`${API_BASE}/admin/logs/cleanup?days=90`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          })
          const data = await res.json()
          if (data.success) {
            message.success(`已清理 ${data.deletedCount} 条日志`)
            loadLogs()
          }
        } catch (err) {
          message.error('清理失败')
        }
      }
    })
  }

  const columns = [
    { title: '时间', dataIndex: 'created_at', width: 180, render: t => t?.substring(0, 19) },
    { title: '用户', dataIndex: 'username', width: 100 },
    { title: '操作', dataIndex: 'action', width: 150,
      render: a => <Tag color="blue">{a}</Tag>
    },
    { title: '方法', dataIndex: 'method', width: 80,
      render: m => <Tag color={m === 'POST' ? 'green' : m === 'DELETE' ? 'red' : 'default'}>{m}</Tag>
    },
    { title: '路径', dataIndex: 'path', ellipsis: true },
    { title: 'IP', dataIndex: 'ip', width: 140 },
    { title: '状态', dataIndex: 'status', width: 80,
      render: s => <Tag color={s === 'success' ? 'green' : 'red'}>{s === 'success' ? '成功' : '失败'}</Tag>
    },
    { title: '操作', width: 80,
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => {
          Modal.info({
            title: '日志详情',
            content: (
              <pre style={{ maxHeight: 400, overflow: 'auto' }}>
                {JSON.stringify(record.details || {}, null, 2)}
              </pre>
            )
          })
        }}>
          详情
        </Button>
      )
    }
  ]

  const actionOptions = [
    { value: 'login', label: '登录' },
    { value: 'logout', label: '登出' },
    { value: 'create', label: '创建' },
    { value: 'update', label: '更新' },
    { value: 'delete', label: '删除' },
    { value: 'approve', label: '审核通过' },
    { value: 'reject', label: '审核拒绝' }
  ]

  return (
    <div style={{ padding: 24 }}>
      <h2>📋 操作日志</h2>

      {/* 统计卡片 */}
      {stats && (
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Card><Statistic title="今日操作" value={stats.todayCount} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="本周操作" value={stats.weekCount} /></Card>
          </Col>
          <Col span={6}>
            <Card><Statistic title="失败操作" value={stats.failedCount} valueStyle={{ color: '#f5222d' }} /></Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic title="成功率" 
                value={stats.weekCount > 0 ? Math.round((1 - stats.failedCount / stats.weekCount) * 100) : 100} 
                suffix="%" 
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 筛选 */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Select placeholder="操作类型" style={{ width: 150 }} allowClear
            onChange={v => setFilters(prev => ({ ...prev, action: v }))}>
            {actionOptions.map(o => <Option key={o.value} value={o.value}>{o.label}</Option>)}
          </Select>
          <RangePicker onChange={(dates, dateStrings) => {
            setFilters(prev => ({ ...prev, startDate: dateStrings[0], endDate: dateStrings[1] }))
          }} />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>搜索</Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>导出</Button>
          <Button danger icon={<DeleteOutlined />} onClick={handleCleanup}>清理旧日志</Button>
        </div>
      </Card>

      {/* 日志表格 */}
      <Card>
        <Table
          columns={columns}
          dataSource={logs}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            onChange: loadLogs
          }}
          size="small"
        />
      </Card>
    </div>
  )
}

export default OperationLogs
